/**
 * 阿里云金融级实人 / 活体 Web 接入（与官方 H5 示例一致，请求走项目 axios 封装）
 * 文档：引入 jsvm_all.js → getMetaInfo() → 请求认证初始化接口 → window.location.href = certifyUrl
 * https://help.aliyun.com/zh/id-verification/financial-grade-id-verification/face-liveness-verification-websdk-client-side-integration
 */
import { initAliyunLoginFaceVerify, confirmAliyunLoginFaceVerify } from '@/api/user'

/** 与文档示例相同的 SDK 地址（全局注入 getMetaInfo） */
const CLOUDAUTH_JS =
  'https://o.alicdn.com/yd-cloudauth/cloudauth-cdn/jsvm_all.js'

/**
 * 加载官方 JS（等价于在 HTML 里写 <script src="...jsvm_all.js">）
 * Soft：超时/失败不抛错，供 Glow 路径直接 init。
 */
export function loadAliyunScript(timeoutMs = 2500) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false)
      return
    }
    if (typeof window.getMetaInfo === 'function') {
      resolve(true)
      return
    }
    let settled = false
    const finish = (ok) => {
      if (settled) return
      settled = true
      resolve(!!ok)
    }
    const timer = setTimeout(() => finish(false), timeoutMs)
    const existing = document.querySelector(
      'script[data-aliyun-cloudauth-jsvm="1"]'
    )
    if (existing) {
      if (existing.getAttribute('data-loaded') === '1') {
        clearTimeout(timer)
        finish(typeof window.getMetaInfo === 'function')
        return
      }
      existing.addEventListener('load', () => {
        clearTimeout(timer)
        finish(typeof window.getMetaInfo === 'function')
      })
      existing.addEventListener('error', () => {
        clearTimeout(timer)
        finish(false)
      })
      return
    }
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = CLOUDAUTH_JS
    script.async = true
    script.dataset.aliyunCloudauthJsvm = '1'
    script.onload = () => {
      script.setAttribute('data-loaded', '1')
      clearTimeout(timer)
      finish(typeof window.getMetaInfo === 'function')
    }
    script.onerror = () => {
      clearTimeout(timer)
      finish(false)
    }
    document.head.appendChild(script)
  })
}

/**
 * Glow 路径可不依赖 metaInfo：尽量取，失败返回 "{}"，不阻断 init。
 */
export async function getMetaInfoSoft(timeoutMs = 2500) {
  try {
    const ok = await loadAliyunScript(timeoutMs)
    if (!ok || typeof window.getMetaInfo !== 'function') {
      return '{}'
    }
    const meta = window.getMetaInfo()
    if (meta == null || meta === '') return '{}'
    return typeof meta === 'string' ? meta : JSON.stringify(meta)
  } catch (e) {
    return '{}'
  }
}

/** 与文档示例一致：从初始化接口返回里取 certifyUrl（优先 resultObject） */
function pickCertifyFromInitResponse(data) {
  if (!data || typeof data !== 'object') {
    return { certifyUrl: '', certifyId: '' }
  }
  const ro = data.resultObject || data.ResultObject || {}
  const certifyUrl =
    ro.certifyUrl ||
    ro.CertifyUrl ||
    data.certifyUrl ||
    data.CertifyUrl ||
    ''
  const certifyId =
    ro.certifyId ??
    ro.CertifyId ??
    data.certifyId ??
    data.CertifyId ??
    ''
  return {
    certifyUrl: certifyUrl || '',
    certifyId: certifyId != null && certifyId !== '' ? String(certifyId) : '',
  }
}

export const LOGIN_FACE_VERIFIED_KEY = 'loginFaceLivenessVerified'
export const LOGIN_FACE_PENDING_KEY = 'loginFaceLivenessPending'

export function isLoginFaceVerified() {
  if (typeof window === 'undefined') return false
  try {
    return sessionStorage.getItem(LOGIN_FACE_VERIFIED_KEY) === '1'
  } catch {
    return false
  }
}

export function buildLoginFaceReturnUrl(route) {
  if (typeof window === 'undefined') return ''
  const u = new URL(route.fullPath, window.location.origin)
  u.searchParams.set('aliyunLoginFace', '1')
  return u.toString()
}

/**
 * 文档步骤：getMetaInfo → POST 初始化（此处为 initAliyunLoginFaceVerify，即 axios）→ 跳转 certifyUrl
 */
export async function startAliyunLoginFaceVerify({ route, phone, telCode }) {
  // Glow 不依赖 jsvm；CloudAuth 兜底时尽量带 metaInfo，取不到用 "{}"
  const metaInfoStr = await getMetaInfoSoft(2500)

  const returnUrl = buildLoginFaceReturnUrl(route)

  const data = await initAliyunLoginFaceVerify({
    metaInfo: metaInfoStr,
    returnUrl,
    phone: phone || undefined,
    telCode: telCode || undefined,
  })

  const { certifyUrl, certifyId } = pickCertifyFromInitResponse(data)
  if (!certifyUrl || !certifyId) {
    throw new Error(
      data?.message ||
        data?.Message ||
        data?.msg ||
        '服务端未返回 resultObject.certifyUrl / certifyId'
    )
  }

  try {
    sessionStorage.setItem(
      LOGIN_FACE_PENDING_KEY,
      JSON.stringify({
        certifyId,
        phone: phone || '',
        telCode: telCode || '',
      })
    )
  } catch {
    throw new Error('无法写入 sessionStorage，请关闭无痕模式或允许站点存储')
  }

  // 与文档一致：获取 CertifyUrl 后跳转至认证页面
  window.location.href = certifyUrl
}

export async function consumeAliyunLoginFaceReturn({ route, router, ElMessage }) {
  const flag = route.query.aliyunLoginFace
  if (flag !== '1' && flag !== 1) return false

  let pending
  try {
    pending = JSON.parse(
      sessionStorage.getItem(LOGIN_FACE_PENDING_KEY) || 'null'
    )
  } catch {
    pending = null
  }

  const q = route.query || {}
  const certifyIdFromUrl = q.certifyId || q.CertifyId || ''
  if (certifyIdFromUrl && pending && !pending.certifyId) {
    pending.certifyId = String(certifyIdFromUrl)
  }
  if (certifyIdFromUrl && !pending) {
    pending = {
      certifyId: String(certifyIdFromUrl),
      phone: '',
      telCode: '',
    }
  }

  const cleanQuery = { ...route.query }
  delete cleanQuery.aliyunLoginFace
  delete cleanQuery.certifyId
  delete cleanQuery.CertifyId
  delete cleanQuery.passed
  delete cleanQuery.callbackToken

  if (!pending?.certifyId) {
    if (ElMessage) {
      ElMessage.warning('未找到认证会话，请重新点击「开始人脸验证」')
    }
    await router.replace({ path: route.path, query: cleanQuery })
    return true
  }

  try {
    await confirmAliyunLoginFaceVerify({
      certifyId: pending.certifyId,
      phone: pending.phone,
      telCode: pending.telCode,
    })
  } catch (e) {
    if (ElMessage) {
      ElMessage.error(e?.message || '认证确认失败')
    }
    sessionStorage.removeItem(LOGIN_FACE_PENDING_KEY)
    await router.replace({ path: route.path, query: cleanQuery })
    return true
  }

  sessionStorage.removeItem(LOGIN_FACE_PENDING_KEY)
  try {
    sessionStorage.setItem(LOGIN_FACE_VERIFIED_KEY, '1')
  } catch {
    /* ignore */
  }
  if (ElMessage) {
    ElMessage.success('人脸认证已通过，请登录')
  }
  await router.replace({ path: route.path, query: cleanQuery })
  return true
}
